import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { LibroDeCurso } from 'models/libro_de_curso_model';
import type { LibroDeCursoTemaForm } from 'models/libro_de_curso_tema_form_model';
import type { LibroDeCursoCirculoDeColoresForm } from 'models/libro_de_curso_circulo_de_colores_form_model';
import type { ActivitiesData } from 'models/libro_de_curso_grid_activities_model';
import { getCourseBookByDate, saveActivities } from 'repositories/libro_de_curso_repository';
import { createTopic } from 'repositories/libro_de_curso_tema_repository';
import { saveCircleOfColors } from 'repositories/libro_de_curso_circulo_de_colores_repository';

interface LibroDeCursoState {
  libroDeCurso: LibroDeCurso | null;
  courseTopicForm: Partial<LibroDeCursoTemaForm> | null;
  circleOfColorsForm: Partial<LibroDeCursoCirculoDeColoresForm> | null;
  loading: boolean;
  error: string | null;
}

const initialState: LibroDeCursoState = {
  libroDeCurso: null,
  courseTopicForm: null,
  circleOfColorsForm: null,
  loading: false,
  error: null,
};

export const cargarLibroDeCurso = createAsyncThunk<LibroDeCurso | null, { semana?: number; anio?: number } | undefined, { rejectValue: string }>(
  'libroDeCurso/cargar',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await getCourseBookByDate(params.semana, params.anio);
    } catch {
      return rejectWithValue('Error al cargar libro de curso');
    }
  },
);

export const addTopic = createAsyncThunk(
  'libroDeCurso/addTopic',
  async (data: LibroDeCursoTemaForm, { rejectWithValue }) => {
    try {
      await createTopic(data);
    } catch {
      return rejectWithValue('Error al crear tema');
    }
  },
);

export const addActivities = createAsyncThunk(
  'libroDeCurso/addActivities',
  async ({ data, fecha }: { data: ActivitiesData; fecha: string }, { rejectWithValue }) => {
    try {
      await saveActivities(data, fecha);
    } catch {
      return rejectWithValue('Error al guardar actividades');
    }
  },
);

export const addCircleOfColors = createAsyncThunk(
  'libroDeCurso/addCircleOfColors',
  async (data: LibroDeCursoCirculoDeColoresForm, { rejectWithValue }) => {
    try {
      await saveCircleOfColors(data);
    } catch {
      return rejectWithValue('Error al guardar círculo de colores');
    }
  },
);

export const libroDeCursoSlice = createSlice({
  name: 'libroDeCurso',
  initialState,
  reducers: {
    setLibroDeCurso: (state, action: PayloadAction<LibroDeCurso>) => {
      state.libroDeCurso = action.payload;
    },
    setCourseTopicForm: (state, action: PayloadAction<Partial<LibroDeCursoTemaForm>>) => {
      state.courseTopicForm = { ...state.courseTopicForm, ...action.payload };
    },
    setCircleOfColorsForm: (
      state,
      action: PayloadAction<Partial<LibroDeCursoCirculoDeColoresForm>>,
    ) => {
      state.circleOfColorsForm = { ...state.circleOfColorsForm, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cargarLibroDeCurso.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarLibroDeCurso.fulfilled, (state, action) => {
        state.loading = false;
        state.libroDeCurso = action.payload;
      })
      .addCase(cargarLibroDeCurso.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setLibroDeCurso, setCourseTopicForm, setCircleOfColorsForm } =
  libroDeCursoSlice.actions;
export default libroDeCursoSlice.reducer;
